import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { Apollo } from "apollo-angular";
import { IDropdownSettings, NgMultiSelectDropDownModule } from "ng-multiselect-dropdown";
import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, effect, inject, resource, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose } from "@angular/material/dialog";

import { WirelessListQueryQuery } from "../../../apis/networkdashboard/graphql/graphql";
import { ApiHelper } from "../../../helpers/apihelper";
import { FulfilmentRequest } from "../../../helpers/self-service/models/types";
import { AuthService } from "../../../services/authservice";
import { DialogIncidentViewModel } from "./dialog-incident.model";
import { WIRELESS_SUBSCRIPTIONS_LOCATIONS_QUERY } from "./dialog-incident.rgql";

@Component({
  selector: "dialog-incident",
  templateUrl: "dialog-incident.html",
  styleUrl: "dialog-incident.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, TranslatePipe, FormsModule, NgMultiSelectDropDownModule, MatDialogClose],
})
export class DialogIncident {
  private data = inject<{ subscription: DialogIncidentViewModel }>(MAT_DIALOG_DATA);

  private readonly apollo = inject(Apollo);
  private readonly api = inject(ApiHelper);
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);

  protected dropdownSettings: IDropdownSettings = {
    idField: "item_id",
    textField: "item_text",
    unSelectAllText: "Unselect All",
    itemsShowLimit: 1,
    allowSearchFilter: true,
  };

  protected __fulfilmentRequest = {
    fullName: this.auth.currentUser().displayName,
    email: this.auth.currentUser().email,
    text: "",
    phone: "",
  };

  private subscription = signal(this.data.subscription);
  private subscriptionId = computed(() => this.subscription().subscriptionId);
  private product = computed(() => this.subscription().product);
  protected isWireless = computed(() => this.product().type === "Wireless");
  protected presentableName = computed(() => this.formatPresentableName(this.subscription()));

  private locationsResource = resource({
    params: () => (this.isWireless() ? { customer: this.auth.viewingCustomer().customerId } : undefined),
    loader: ({ params }) =>
      firstValueFrom(
        this.apollo.query<WirelessListQueryQuery>({
          query: WIRELESS_SUBSCRIPTIONS_LOCATIONS_QUERY,
          variables: { customer: params.customer },
        }),
      ).then(({ data }) =>
        (data?.subscriptions.page ?? [])
          .map((subscription) => ({
            item_id: subscription.subscriptionId,
            item_text: `${subscription.location.city} - ${this.formatPresentableName(subscription)}`,
          }))
          .sort((a, b) => a.item_text.localeCompare(b.item_text)),
      ),
  });

  protected dropdownLoaded = computed(() => this.locationsResource.hasValue());
  protected dropdownList = computed(() => this.locationsResource.value() ?? []);
  protected selectedItems: { item_id: string; item_text: string }[] = [];

  protected errorMessage = signal("");
  protected successMessage = signal("");

  constructor() {
    effect(() => {
      if (this.locationsResource.hasValue()) {
        this.selectedItems = this.dropdownList().filter(({ item_id }) => item_id === this.subscriptionId());
      }
    });
  }

  private formatPresentableName(subscription: { customerDescription: string; description: string }) {
    return subscription.customerDescription || subscription.description;
  }

  protected async notifySupport() {
    this.successMessage.set("");
    this.errorMessage.set("");

    if (!this.__fulfilmentRequest.text || this.__fulfilmentRequest.text.length < 10) {
      this.errorMessage.set(this.translate.instant("Selfservice.InvalidDescription"));
      return;
    }

    if (!this.__fulfilmentRequest.fullName || this.__fulfilmentRequest.fullName.length < 5) {
      this.errorMessage.set(this.translate.instant("Selfservice.InvalidFullname"));
      return;
    }

    if (!this.__fulfilmentRequest.phone || this.__fulfilmentRequest.phone.length < 5) {
      this.errorMessage.set(this.translate.instant("Selfservice.InvalidPhone"));
      return;
    }

    const customerId = this.auth.viewingCustomer().customerId;
    const locationText = this.selectedItems.map((item) => `- ${item.item_text}`).join("\n");

    const requestMessage = [
      this.__fulfilmentRequest.text,
      `${this.translate.instant("Selfservice.Locations")}: `,
      locationText,
    ].join("\n\n");

    const request: FulfilmentRequest = {
      contacts: [
        {
          fullName: this.__fulfilmentRequest.fullName,
          email: this.__fulfilmentRequest.email,
          phone: this.__fulfilmentRequest.phone,
        },
      ],
      serviceRequest: "problem",
      text: requestMessage,
      subscriptionId: this.subscriptionId(),
      product: this.product().type,
    };

    try {
      await this.api.add_new_fulfilment_problem(customerId, JSON.stringify(request));
      this.successMessage.set(this.translate.instant("Selfservice.NotificationSent"));
    } catch (err) {
      this.errorMessage.set(err instanceof Error ? err.message : String(err));
    }
  }
}

/**
 * Function to help instantiating DialogIncidentComponent with the right input data.
 *
 * Prefer calling this function over directly calling `dialog.open(DialogIncidentComponent, data)`
 * because the latter doesn't help enforce that `data` is of type DialogIncidentModel.
 *
 * @param dialog A mat dialog reference with which to open the incident dialog
 * @param subscription The data to populate the incident dialog with
 * @returns The `MatDialogRef` returned by `dialog.open`
 */
export function openDialogIncident(dialog: MatDialog, subscription: DialogIncidentViewModel) {
  const data = { data: { subscription } };
  return dialog.open(DialogIncident, data);
}
